<template>
    <v-row>
        <v-col>
            <p>
                A space administrator will be able to modify the contents of or start applications in all current and future instances of this space, and will
                be able to invite new people, create new instances, delete instances or the space itself.
            </p>
            <v-card-text class="pa-0">
                <InviteEmailsInput
                    v-model="emailsInput"
                    :valid.sync="valid"
                    :disabled="numInvitationsToSend > 0"
                    :showGenerateLinkOnlyToggle="true"
                    :generateInvitationLinksOnly.sync="generateInvitationLinksOnly"
                    :invitationErrorState="{
                        showDialog: showInvitationErrorsDialog,
                        errorType: validationErrorType,
                        invalidEmails: invalidEmailsList,
                        foundInvalidEmails,
                        backendError,
                        emailsValidationError
                    }"
                    @input="updateEmailString"
                    @proceedWithTheInvitation="inviteAdmins($event.value)"
                    @validateEmailsAgain="validateEmailsAndinviteAdmins($event.value)" />
            </v-card-text>
            <v-card-actions v-if="error">
                <v-alert max-width="600" color="error" icon="warning" class="mt-4" text>
                    <div class="d-flex flex-column">
                        <span class="font-weight-bold">Invitation Failure Error</span>
                        Failed to invite the following users
                        <ul>
                            <li v-for="(email, index) in failedInvitations" :key="index">
                                {{ email }}
                            </li>
                        </ul>
                        <span v-if="invitationErrorContent">{{ invitationErrorContent }}</span>
                    </div>
                </v-alert>
            </v-card-actions>
        </v-col>
    </v-row>
</template>

<script>
import { splitEmails } from '@/utils'
import { emailValidation } from '@/mixins/validateEmail'
import InviteEmailsInput from '@/components/InviteEmailsInput.vue'
import { createBackendErrorHandler } from '@/utils/errorMessages'
import * as invitationsErrors from '@/mixins/errors/invitations'
import * as lifecycleErrors from '@/modules/snapshot/errors/lifecycle'
import * as permissionsErrors from '@/utils/errors/permissions'
import * as spaceErrors from '@/modules/space/errors'

const { getBackendErrorMessage } = createBackendErrorHandler(invitationsErrors, lifecycleErrors, permissionsErrors, spaceErrors)

export default {
    mixins: [emailValidation],
    components: { InviteEmailsInput },
    props: {
        spaceId: {
            type: [Number, String],
            default: null
        }
    },
    data() {
        return {
            error: false,
            emailsInput: '',
            emailList: [],
            failedInvitations: [],
            invitationErrorContent: '',
            valid: false,
            numInvitationsToSend: 0,
            generateInvitationLinksOnly: false
        }
    },
    computed: {
        validationErrorType() {
            if (this.foundInvalidEmails) {
                return this.emailValidationErrorTypes.INVALID_EMAILS_ERROR
            } else if (this.backendError || this.emailsValidationError) {
                return this.emailValidationErrorTypes.EMAIL_VALIDATION_BACKEND_ERROR
            }
            return null
        },
        showInvitationErrorsDialog() {
            if (this.foundInvalidEmails || this.backendError || this.emailsValidationError) {
                return true
            }
            return false
        }
    },
    methods: {
        emitInviteActionState() {
            this.$emit('invite-action-state', {
                visible: true,
                loading: this.numInvitationsToSend > 0 || this.validatingEmails,
                disabled: !this.valid || this.numInvitationsToSend !== 0 || this.validatingEmails
            })
        },
        triggerInviteFromParent() {
            this.validateEmailsAndinviteAdmins(true)
        },
        updateEmailString: function () {
            this.validatedEmails = []
            const emails = splitEmails(this.emailsInput)
            this.emailList = emails.map(email => email.email)
        },
        inviteAdmins: function (confirm) {
            if (confirm) {
                this.error = false
                this.invitationErrorContent = ''
                const resolvedSpaceId = this.spaceId || this.$route.params.sid
                const apiURL = '/invitations/invite_space_admin/' + resolvedSpaceId
                this.$store.dispatch('showSnackBar', {
                    snackBarText: 'Inviting users...',
                    snackBarTimeout: 5000,
                    snackBarIcon: 'info'
                })
                this.numInvitationsToSend = this.emailList.length
                this.emailList.forEach(email => {
                    const postBody = { requestee_email: email, generate_link_only: this.generateInvitationLinksOnly }
                    this.$axios
                        .post(apiURL, postBody, { timeout: 300000 })
                        .then(response => {})
                        .catch(error => {
                            this.failedInvitations.push(email)
                            this.error = true
                            const invitationErrorContent = getBackendErrorMessage(error, '')
                            if (invitationErrorContent) this.invitationErrorContent = invitationErrorContent
                        })
                        .finally(() => {
                            this.numInvitationsToSend -= 1
                            if (this.numInvitationsToSend === 0) {
                                this.$store.dispatch('spaceStore/fetchSpaceInvitations', resolvedSpaceId)
                                this.generateInvitationLinksOnly = false
                                this.emailsInput = ''
                                if (!this.error) {
                                    this.$store.dispatch('showSnackBar', {
                                        snackBarText: 'Invitations have been successfully sent. Invited users will receive an email to accept the invitation.',
                                        snackBarTimeout: 5000,
                                        snackBarIcon: 'check_circle'
                                    })
                                    this.$emit('invite-success')
                                }
                            }
                        })
                })
            }
        },
        validateEmailsAndinviteAdmins: function (confirmed) {
            if (confirmed) {
                this.validateEmails(this.emailList).finally(() => {
                    if (!this.emailsValidationError && !this.backendError && !this.foundInvalidEmails) {
                        this.inviteAdmins(true)
                    }
                })
            }
        }
    },
    mounted() {
        this.emitInviteActionState()
    },
    watch: {
        valid() {
            this.emitInviteActionState()
        },
        numInvitationsToSend() {
            this.emitInviteActionState()
        },
        validatingEmails() {
            this.emitInviteActionState()
        }
    }
}
</script>
